import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import QRCode from 'react-qr-code';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-cards';
import { EffectCards } from 'swiper/modules';
import { 
  Stamp, Gift, Check, Loader2, QrCode, Heart, Star, Coffee, Pizza, Flame, Diamond,
  Crown, Leaf, Zap, Trophy, Music, Sun, Cake, Flower2, Snowflake, Scissors, Wine, IceCreamCone,
  Dog, Car, Wrench, Book, Anchor, MapPin, Mountain, TreePine, Waves, Compass,
  Sparkles, ShoppingBag, Shirt, Dumbbell, Stethoscope, GraduationCap, Gamepad2, Headphones, Brush,
  Award, Wallet, ChevronLeft, ChevronRight, Apple, Smartphone
} from 'lucide-react';

const STAMP_ICON_MAP = {
  stamp: Stamp, heart: Heart, star: Star, coffee: Coffee,
  pizza: Pizza, flame: Flame, check: Check, diamond: Diamond,
  crown: Crown, leaf: Leaf, zap: Zap, trophy: Trophy,
  music: Music, sun: Sun, cake: Cake, flower: Flower2,
  snowflake: Snowflake, sparkles: Sparkles, dog: Dog
};

const CARD_ICON_MAP = {
  coffee: Coffee, pizza: Pizza, cake: Cake, scissors: Scissors, shoppingbag: ShoppingBag,
  dumbbell: Dumbbell, heart: Heart, flower: Flower2, wine: Wine, icecream: IceCreamCone,
  dog: Dog, car: Car, book: Book, music: Music, stethoscope: Stethoscope, crown: Crown,
  leaf: Leaf, zap: Zap, sparkles: Sparkles, gift: Gift, star: Star, diamond: Diamond,
  flame: Flame, trophy: Trophy, sun: Sun, mountain: Mountain, anchor: Anchor, compass: Compass,
  wrench: Wrench, shirt: Shirt, graduation: GraduationCap, gamepad: Gamepad2, headphones: Headphones,
  brush: Brush, snowflake: Snowflake, treepine: TreePine, waves: Waves, mappin: MapPin, stamp: Stamp
};

const getStampIcon = (iconId, className, style) => {
  const Icon = STAMP_ICON_MAP[iconId] || Stamp;
  return <Icon className={className} style={style} />;
};

const getPatternStyle = (pattern) => {
  const w = 'rgba(255,255,255,';
  switch (pattern) {
    case 'cercles': return { backgroundImage: `radial-gradient(circle, transparent 7px, ${w}0.08) 7px, ${w}0.08) 8px, transparent 8px)`, backgroundSize: '22px 22px' };
    case 'points': case 'dots': return { backgroundImage: `radial-gradient(${w}0.15) 2px, transparent 2px)`, backgroundSize: '12px 12px' };
    case 'vagues': case 'waves': return { backgroundImage: `repeating-linear-gradient(45deg, ${w}0.07) 0px, ${w}0.07) 1px, transparent 1px, transparent 10px), repeating-linear-gradient(-45deg, ${w}0.07) 0px, ${w}0.07) 1px, transparent 1px, transparent 10px)` };
    case 'diagonales': return { backgroundImage: `repeating-linear-gradient(45deg, ${w}0.1) 0, ${w}0.1) 1px, transparent 1px, transparent 8px)` };
    case 'damier': return { backgroundImage: `linear-gradient(45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(-45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${w}0.08) 75%), linear-gradient(-45deg, transparent 75%, ${w}0.08) 75%)`, backgroundSize: '16px 16px', backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px' };
    case 'grille': return { backgroundImage: `linear-gradient(${w}0.08) 1px, transparent 1px), linear-gradient(90deg, ${w}0.08) 1px, transparent 1px)`, backgroundSize: '20px 20px' };
    case 'zigzag': return { backgroundImage: `linear-gradient(135deg, ${w}0.1) 25%, transparent 25%), linear-gradient(225deg, ${w}0.1) 25%, transparent 25%), linear-gradient(315deg, ${w}0.1) 25%, transparent 25%), linear-gradient(45deg, ${w}0.1) 25%, transparent 25%)`, backgroundSize: '20px 20px', backgroundPosition: '0 0, 10px 0, 10px -10px, 0 10px' };
    case 'confettis': case 'confetti': return { backgroundImage: `radial-gradient(${w}0.12) 1.5px, transparent 1.5px), radial-gradient(${w}0.12) 1.5px, transparent 1.5px)`, backgroundSize: '16px 16px', backgroundPosition: '0 0, 8px 8px' };
    case 'rayures': return { backgroundImage: `repeating-linear-gradient(90deg, ${w}0.08) 0, ${w}0.08) 2px, transparent 2px, transparent 10px)` };
    case 'losanges': return { backgroundImage: `linear-gradient(45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(-45deg, ${w}0.08) 25%, transparent 25%)`, backgroundSize: '16px 16px' };
    case 'triangles': return { backgroundImage: `linear-gradient(60deg, ${w}0.07) 25%, transparent 25%), linear-gradient(-60deg, ${w}0.07) 25%, transparent 25%)`, backgroundSize: '18px 14px' };
    case 'croix': return { backgroundImage: `linear-gradient(${w}0.1) 2px, transparent 2px), linear-gradient(90deg, ${w}0.1) 2px, transparent 2px)`, backgroundSize: '12px 12px' };
    case 'etoiles': return { backgroundImage: `radial-gradient(${w}0.15) 1px, transparent 1px), radial-gradient(${w}0.1) 1px, transparent 1px)`, backgroundSize: '10px 10px', backgroundPosition: '0 0, 5px 5px' };
    case 'bulles': return { backgroundImage: `radial-gradient(circle, ${w}0.08) 4px, transparent 4px), radial-gradient(circle, ${w}0.05) 3px, transparent 3px)`, backgroundSize: '24px 24px', backgroundPosition: '0 0, 12px 12px' };
    case 'circles': return { backgroundImage: `radial-gradient(circle, transparent 6px, ${w}0.06) 6px, ${w}0.06) 7px, transparent 7px)`, backgroundSize: '20px 20px' };
    default: return {};
  }
};

const LoyaltyWalletCard = ({ card, qrData }) => {
  const color = card.card_color || '#059669';
  const pattern = card.card_pattern || 'none';
  const icon = card.stamp_icon || 'stamp';
  const logo = card.loyalty_logo || card.restaurant_logo;
  const patternBg = getPatternStyle(pattern);
  const shortId = card.id.slice(0, 8).toUpperCase();
  const CardIconComp = CARD_ICON_MAP[card.card_icon] || Gift;
  const remaining = card.stamps_required - card.stamps;

  return (
    <div className="rounded-3xl overflow-hidden shadow-2xl relative" style={{ backgroundColor: color, ...patternBg }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="" className="h-10 w-10 rounded-xl object-cover bg-white/20" />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                <CardIconComp className="h-5 w-5 text-white/70" />
              </div>
            )}
            <div>
              <p className="text-[9px] font-semibold text-white/50 uppercase tracking-widest">Carte fidélité</p>
              <p className="font-bold text-white text-lg leading-tight">{card.restaurant_name}</p>
            </div>
          </div>
          <div className="px-2.5 py-1 rounded-lg bg-white/15 text-white text-[9px] font-bold uppercase tracking-wider">
            {card.stamps}/{card.stamps_required}
          </div>
        </div>
      </div>

      {/* Stamps + QR section */}
      <div className="px-5 py-4 flex gap-4">
        {qrData && (
          <div className="flex-shrink-0 bg-white rounded-xl p-2 shadow-lg">
            <QRCode
              value={JSON.stringify({ client_id: qrData.client_id, email: qrData.email })}
              size={80}
              level="M"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Array.from({ length: card.stamps_required }).map((_, i) => (
              <div key={i} className="h-7 w-7 rounded-full flex items-center justify-center"
                style={{ backgroundColor: i < card.stamps ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.15)' }}>
                {i < card.stamps && getStampIcon(icon, 'h-3.5 w-3.5', { color })}
              </div>
            ))}
          </div>
          <p className="text-white/70 text-xs font-medium italic">
            {remaining > 0 ? `Plus que ${remaining} tampon${remaining > 1 ? 's' : ''} !` : 'Récompense disponible !'}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Gift className="h-3 w-3 text-white/50" />
            <p className="text-white/50 text-[10px]">{card.reward_text}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mx-5 border-t border-white/10" />
      <div className="px-5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white/30 text-[9px] font-mono">{shortId}</span>
          {card.completed_count > 0 && (
            <span className="flex items-center gap-1 text-white/40 text-[9px]">
              <Award className="h-2.5 w-2.5" />
              {card.completed_count} cadeau(x)
            </span>
          )}
        </div>
        <span className="text-white/25 text-[9px] font-semibold">ZOTMENU</span>
      </div>
    </div>
  );
};

const LoyaltyCards = () => {
  const [cards, setCards] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showQr, setShowQr] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, qrRes] = await Promise.all([
        api.get('/loyalty/my-cards'),
        api.get('/loyalty/qr-data')
      ]);
      setCards(cardsRes.data || []);
      setQrData(qrRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const totalStamps = cards.reduce((sum, c) => sum + c.stamps, 0);
  const totalRewards = cards.reduce((sum, c) => sum + (c.completed_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="loyalty-cards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Wallet className="h-8 w-8 text-orange-500" />
            Mon Wallet
          </h1>
          <p className="text-gray-500 mt-1">
            {cards.length} carte{cards.length > 1 ? 's' : ''} de fidélité
          </p>
        </div>
        <Button
          onClick={() => setShowQr(!showQr)}
          variant={showQr ? "default" : "outline"}
          className={`rounded-full ${showQr ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
          data-testid="toggle-qr-btn"
        >
          <QrCode className="h-4 w-4 mr-2" />
          Mon QR
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-soft">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{cards.length}</p>
            <p className="text-xs text-gray-500">Carte{cards.length > 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-soft">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-500">{totalStamps}</p>
            <p className="text-xs text-gray-500">Tampons</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-soft">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-500">{totalRewards}</p>
            <p className="text-xs text-gray-500">Cadeau{totalRewards > 1 ? 'x' : ''}</p>
          </CardContent>
        </Card>
      </div>

      {/* QR Code (togglable) */}
      {showQr && qrData && (
        <Card className="border-0 shadow-soft overflow-hidden" data-testid="my-qr-card">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-500 mb-4">
              Présentez ce code au commerçant
            </p>
            <div className="inline-block bg-white p-5 rounded-2xl shadow-md border border-gray-100">
              <QRCode
                value={JSON.stringify({ client_id: qrData.client_id, email: qrData.email })}
                size={200}
                level="M"
              />
            </div>
            <div className="mt-4 inline-block px-5 py-2 rounded-full bg-orange-50">
              <span className="font-mono font-bold text-lg tracking-[0.3em] text-orange-600">
                {qrData.client_id.slice(0, 8).toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-2">{user?.email}</p>
          </CardContent>
        </Card>
      )}

      {/* Wallet Cards */}
      {cards.length === 0 ? (
        <Card className="border-0 shadow-soft">
          <CardContent className="p-8 text-center">
            <Wallet className="h-14 w-14 mx-auto mb-4 text-gray-200" />
            <p className="text-gray-500 font-medium">Votre wallet est vide</p>
            <p className="text-sm text-gray-400 mt-1">Présentez votre QR code dans un restaurant participant pour commencer</p>
          </CardContent>
        </Card>
      ) : cards.length === 1 ? (
        /* Single card — no swiper needed */
        <div className="max-w-sm mx-auto">
          <LoyaltyWalletCard card={cards[0]} qrData={qrData} />
        </div>
      ) : (
        /* Multiple cards — swipeable wallet */
        <div>
          <div className="max-w-sm mx-auto">
            <Swiper
              effect="cards"
              grabCursor={true}
              modules={[EffectCards]}
              onSlideChange={(s) => setActiveIndex(s.activeIndex)}
              className="!overflow-visible"
            >
              {cards.map((card) => (
                <SwiperSlide key={card.id} className="!rounded-3xl">
                  <LoyaltyWalletCard card={card} qrData={qrData} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Card indicator dots */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {cards.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${
                  i === activeIndex ? 'w-6 bg-orange-500' : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Swipe hint */}
          <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Glissez pour changer de carte <ChevronRight className="h-3 w-3" />
          </p>
        </div>
      )}
    </div>
  );
};

export default LoyaltyCards;
